import { createFeatureSelector, createSelector } from '@ngrx/store';
import { isEqual } from 'lodash';
import * as moment from 'moment';

import { ComparisonMode, SortOrder } from '@app/services/summary.service';
import {
  FilterByFieldInfoModel,
  PeriodType,
  SummaryReportSettingsModel,
  SummaryType,
} from '@main-application/shared/services/summary-report-settings.service';

import { SummaryReportSettingsState } from '../reducers/summary-report-settings.reducers';

export const selectSummaryReportSettingsState =
  createFeatureSelector<SummaryReportSettingsState>('summaryReportSettings');

export const selectSummarySettingsMap = createSelector(
  selectSummaryReportSettingsState,
  state => state.summarySettings
);

export const selectCombinedSummarySetting = (type: SummaryType) =>
  createSelector(selectSummaryReportSettingsState, state => state.summarySettings?.[type] ?? null);

export const selectInitialSummarySetting = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => settings[type]?.initial);

export const selectModifiedSummarySetting = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => settings[type]?.modified);

export const selectSummarySettingDirty = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => {
    const s: { initial: SummaryReportSettingsModel; modified: SummaryReportSettingsModel } = settings[type];
    if (!s?.initial || !s?.modified) return false;

    const { initial, modified } = s;

    const shouldCompareDates = initial.periodType === PeriodType.Custom || modified.periodType === PeriodType.Custom;
    const shouldCompareBDates = initial.periodBType === PeriodType.Custom || modified.periodBType === PeriodType.Custom;

    const normalizeDate = (value: any): string | null => {
      if (value === '' || value === undefined || value === null) return null;
      const m = moment(value);
      return m.isValid() ? m.format('YYYY-MM-DD') : null;
    };

    const startDate = normalizeDate(initial.startDate);
    const endDate = normalizeDate(initial.endDate);
    const modifiedStartDate = normalizeDate(modified.startDate);
    const modifiedEndDate = normalizeDate(modified.endDate);

    const startBDate = normalizeDate(initial.periodBStartDate);
    const endBDate = normalizeDate(initial.periodBEndDate);
    const modifiedStartBDate = normalizeDate(modified.periodBStartDate);
    const modifiedEndBDate = normalizeDate(modified.periodBEndDate);

    const flags = {
      name: initial.name !== modified.name,
      description: initial.description !== modified.description,
      summaryType: initial.summaryType !== modified.summaryType,
      dataSource: initial.dataSource !== modified.dataSource,
      dataSourceModifier: initial.dataSourceModifier !== modified.dataSourceModifier,
      reportLayout: (initial.reportLayout ?? 1) !== (modified.reportLayout ?? 1),
      vsModeEnabled: (initial.vsModeEnabled ?? false) !== (modified.vsModeEnabled ?? false),
      vsModeComparisonMode:
        (initial.vsModeComparisonMode ?? ComparisonMode.Value) !==
        (modified.vsModeComparisonMode ?? ComparisonMode.Value),
      periodType: initial.periodType !== modified.periodType,
      periodDateSearchFields: initial.periodDateSearchFields !== modified.periodDateSearchFields,
      periodBType:
        initial.periodBType !== modified.periodBType && !(initial.periodBType == null && modified.periodBType == null),
      reportView: initial.reportView !== modified.reportView,
      sortBy: !isEqual(initial.sortBy, modified.sortBy),
      dates: shouldCompareDates && (startDate !== modifiedStartDate || endDate !== modifiedEndDate),
      showBy:
        type === SummaryType.ServiceList || type === SummaryType.InspectionList
          ? false
          : !isEqual(
              initial.showBy,
              modified.showBy?.map(s => ({
                fieldType: s.fieldType,
                isDescending: s.isDescending || s.sortOrder === SortOrder.Descending,
              }))
            ),
      bDates:
        shouldCompareBDates &&
        ((startBDate !== modifiedStartBDate && !(startBDate == null && modifiedStartBDate == null)) ||
          (endBDate !== modifiedEndBDate && !(endBDate == null && modifiedEndBDate == null))),
      filterBy: !areFilterByArraysEqual(initial.filterBy, modified.filterBy),
      rolesCanView: !isEqual(initial.rolesCanView, modified.rolesCanView),
      rolesCanEdit: !isEqual(initial.rolesCanEdit, modified.rolesCanEdit),
    };

    const result = Object.values(flags).some(Boolean);
    return result;
  });

export const selectAllSummaryReportSettings = createSelector(selectSummaryReportSettingsState, state => state.settings);

export const selectSummaryReportSettingsLoading = createSelector(
  selectSummaryReportSettingsState,
  state => state.loading
);

export const selectSummaryReportSettingsError = createSelector(selectSummaryReportSettingsState, state => state.error);

export const selectIsDownloadingReport = createSelector(selectSummaryReportSettingsState, state => state.downloading);

function areFilterByArraysEqual(
  a: FilterByFieldInfoModel[] | undefined,
  b: FilterByFieldInfoModel[] | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;

  const normalizeValue = (val: string | undefined | null): string | undefined => {
    if (val === '' || val === null || val === undefined) return undefined;
    return val;
  };

  return a.every((itemA, index) => {
    const itemB = b[index];
    if (!itemB) return false;

    return (
      itemA.fieldType === itemB.fieldType &&
      normalizeValue(itemA.values) === normalizeValue(itemB.values) &&
      itemA.isSingleChoiceSelector === itemB.isSingleChoiceSelector &&
      normalizeValue(itemA.labels) === normalizeValue(itemB.labels)
    );
  });
}
